'use client';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import { useAuthStore } from '@/lib/store/useAuthStore';
import { getExchangeCodeTokenFromCookie, isTokenExpired } from '@/lib/utils/cookieUtils';
import { useRetrieveToken } from '@/lib/services/auth';
import { getAgentMe, DecodedUserToken } from '@/lib/services/agent';

const MAX_RETRY_ATTEMPTS = 5;

/**
 * Detecta se está rodando em ambiente mobile (webview)
 * Mobile é identificado quando o exchangeCode vem via URL
 */
const getExchangeCodeFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('exchangeCode');
};

export default function AuthBootstrap() {
  const {
    jwtTokens,
    squadToken,
    setJwtTokens,
    setExchangeCodeToken,
    setToken,
    setAuthStatus,
    exchangeCodeToken,
  } = useAuthStore();
  const { mutate: retrieveToken, isPending } = useRetrieveToken();
  const hasBootstrapped = useRef(false);
  const retryCountRef = useRef(0);
  const hasShownErrorToast = useRef(false);

  useEffect(() => {
    if (hasBootstrapped.current || isPending) return;

    if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
      console.warn('[AuthBootstrap] Limite de tentativas atingido, parando retry');
      if (!hasShownErrorToast.current) {
        hasShownErrorToast.current = true;
        toast.error('Erro de autenticação. Faça logout e login novamente no Smartspace.', {
          duration: 8000,
        });
      }
      setAuthStatus('external');
      return;
    }

    const bootstrapAuth = async () => {
      const urlExchangeCode = getExchangeCodeFromUrl();
      const cookieExchangeCode = getExchangeCodeTokenFromCookie();
      const exchangeCode = urlExchangeCode || cookieExchangeCode;
      const isMobile = !!urlExchangeCode;

      if (isMobile) {
        console.log('[AuthBootstrap] 📱 Mobile mode detected (exchangeCode from URL)');
      }

      if (!exchangeCode) {
        console.log('[AuthBootstrap] No exchange code found, marking as external user');
        setAuthStatus('external');
        hasBootstrapped.current = true;
        return;
      }

      const hasValidToken = jwtTokens?.accessToken && !isTokenExpired(jwtTokens.accessToken);
      const hasSquadToken = !!squadToken;
      const sameExchangeCode = exchangeCode === exchangeCodeToken;

      if (hasValidToken && hasSquadToken && sameExchangeCode) {
        console.log('[AuthBootstrap] ✅ Tokens already valid in Zustand, no action needed');
        setAuthStatus('authenticated');
        hasBootstrapped.current = true;
        return;
      }

      console.log('[AuthBootstrap] Retrieving new tokens...', {
        source: isMobile ? 'URL (mobile)' : 'cookie (web)',
        exchangeCodeChanged: !sameExchangeCode,
        hasValidToken,
        hasSquadToken,
      });

      hasBootstrapped.current = true;
      retryCountRef.current += 1;

      retrieveToken(
        { uuid: exchangeCode },
        {
          onSuccess: async (data) => {
            try {
              setJwtTokens({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
              });
              setExchangeCodeToken(exchangeCode);

              const userDecoded = jwtDecode<DecodedUserToken>(data.accessToken);
              console.log('[AuthBootstrap] JWT decoded:', {
                domain: userDecoded.domain,
                email: userDecoded.email,
                userName: userDecoded.userName,
              });

              const agent = await getAgentMe();
              console.log('[AuthBootstrap] Agent /me response:', {
                id: agent.id,
                name: agent.name,
              });

              const newSquadToken = {
                access_token: userDecoded.voice,
                domain: userDecoded.domain,
                baseUrl: userDecoded.voiceUrl,
                email: userDecoded.email,
                user_name: userDecoded.userName,
                expires_in: userDecoded.exp,
                user_id: agent.id,
              };

              setToken(newSquadToken);
              setAuthStatus('authenticated');

              console.log('[AuthBootstrap] ✅ Authentication complete (persisted via Zustand)');
              retryCountRef.current = 0;
            } catch (error) {
              console.error('[AuthBootstrap] Error processing tokens:', error);
              setAuthStatus('external');
              retryCountRef.current = 0;
            }
          },
          onError: (error) => {
            console.error('[AuthBootstrap] Failed to retrieve tokens', error);

            if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
              hasBootstrapped.current = false;
            } else {
              console.warn('[AuthBootstrap] Máximo de tentativas atingido');
              setAuthStatus('external');
              if (!hasShownErrorToast.current) {
                hasShownErrorToast.current = true;
                toast.error('Erro de autenticação. Faça logout e login novamente no Smartspace.', {
                  duration: 8000,
                });
              }
            }
          },
        },
      );
    };

    bootstrapAuth();
  }, [
    jwtTokens,
    squadToken,
    exchangeCodeToken,
    setJwtTokens,
    setExchangeCodeToken,
    setToken,
    setAuthStatus,
    retrieveToken,
    isPending,
  ]);

  return null;
}
